
import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { MonitoringEvent, MonitoringBuffer } from '@/types/monitoring';
import { useAuth } from '@/contexts/AuthContext';

// Buffer size and flush interval constants
const BUFFER_SIZE = 10;
const FLUSH_INTERVAL = 15000; // 15 seconds

export function useBufferedMonitoring(testResponseId: string) {
  const { user } = useAuth();
  const [buffer, setBuffer] = useState<MonitoringBuffer>({
    events: [],
    testResponseId
  });
  const flushTimerRef = useRef<number | null>(null);
  const isFlushingRef = useRef(false);

  // Reset buffer when testResponseId changes
  useEffect(() => {
    setBuffer({
      events: [],
      testResponseId
    });
  }, [testResponseId]);

  // Set up periodic flushing
  useEffect(() => {
    const setupFlushTimer = () => {
      if (flushTimerRef.current) {
        window.clearTimeout(flushTimerRef.current);
      }
      
      flushTimerRef.current = window.setTimeout(async () => {
        await flushBuffer();
        setupFlushTimer();
      }, FLUSH_INTERVAL);
    };
    
    setupFlushTimer();
    
    return () => {
      if (flushTimerRef.current) {
        window.clearTimeout(flushTimerRef.current);
      }
    };
  }, []);

  const flushBuffer = useCallback(async () => {
    // Don't flush if we're already flushing or if buffer is empty
    if (isFlushingRef.current || buffer.events.length === 0 || !user) {
      return;
    }
    
    try {
      isFlushingRef.current = true;
      console.log(`[Monitoring] Flushing ${buffer.events.length} events`);
      
      // Create individual inserts for each event
      for (const event of buffer.events) {
        try {
          // Explicitly cast the event_type to ensure TypeScript recognizes it as a valid enum value
          const { error } = await supabase
            .from('test_monitoring_events')
            .insert({
              test_response_id: testResponseId,
              event_type: event.event_type,
              event_data: event.event_data,
              severity: event.severity,
              duration: event.duration,
              question_id: event.question_id,
              device_fingerprint: event.device_fingerprint,
              ai_confidence_score: event.ai_confidence_score,
              created_at: event.created_at
            });
            
          if (error) {
            console.error('[Monitoring] Error inserting event:', error);
          }
        } catch (err) {
          console.error('[Monitoring] Error inserting event:', err);
        }
      }
      
      // Clear buffer after successful flush
      setBuffer({
        events: [],
        testResponseId
      });
      
      console.log('[Monitoring] Events flushed successfully');
    } catch (err) {
      console.error('[Monitoring] Error during flush:', err);
    } finally {
      isFlushingRef.current = false;
    }
  }, [buffer, testResponseId, user]);

  const logEventBuffered = useCallback(async (event: Omit<MonitoringEvent, 'test_response_id'>) => {
    // Add event to buffer
    setBuffer(prev => {
      const newEvents = [...prev.events, event];
      
      // If buffer is full, trigger a flush
      if (newEvents.length >= BUFFER_SIZE && !isFlushingRef.current) {
        flushBuffer();
      }
      
      return {
        events: newEvents,
        testResponseId
      };
    });
  }, [testResponseId, flushBuffer]);

  const forceFlush = useCallback(async () => {
    console.log('[Monitoring] Force flushing events');
    return flushBuffer();
  }, [flushBuffer]);

  return {
    logEventBuffered,
    forceFlush
  };
}
